// Target table processing module: manipulation functions for document and study extract tables
// These functions work with 2D arrays (as used in the grid UI) and handle conversion internally
import { logWarning, logError, logInfo } from "../../../services/logging/log-handler.js";

export type TargetExtract = any;

// Import and re-export conversion functions from dataConversion.ts for convenience
import {
  documentExtractTableTo2DArray,
  studyTableTo2DArray,
  arrayToStudyTable,
  referenceTo2DArray
} from './data-conversion.js';

export {
  documentExtractTableTo2DArray,
  studyTableTo2DArray,
  arrayToStudyTable,
  referenceTo2DArray
};

import { generateUUID } from './uuid-utils.js';

/**
 * Get target table data as 2D array from extract
 * @param extractData - Document or study extract
 * @param topicId - Topic ID
 * @param tableId - Table ID
 * @param projectStructure - Project structure
 * @param extractType - 'document' or 'study'
 * @returns 2D array with headers in first row
 */
export function getTargetData2D(
  extractData: TargetExtract,
  topicId: string,
  tableId: string,
  projectStructure: any,
  extractType: 'document' | 'study'
): any[][] {
  if (extractType === 'document') {
    return documentExtractTableTo2DArray(topicId, tableId, extractData, projectStructure);
  } else {
    return studyTableTo2DArray(topicId, tableId, extractData, projectStructure);
  }
}

/**
 * Add a new row to a target table (working with 2D array format)
 * @param data2D - 2D array with headers in first row
 * @returns Updated 2D array with new empty row
 */
export function addTargetRow(data2D: any[][]): any[][] {
  if (!data2D || data2D.length === 0) return data2D;
  
  const updated = JSON.parse(JSON.stringify(data2D));
  const headers = updated[0];
  
  // Create empty row with same number of columns as headers
  const newRow: any[] = [];
  headers.forEach(() => {
    newRow.push('');
  });
  
  // Add new row after header row
  updated.push(newRow);
  
  return updated;
}

/**
 * Remove rows from a target table (working with 2D array format)
 * @param data2D - 2D array with headers in first row
 * @param rowIndices - Array of row indices to remove (0-based, excluding header)
 * @returns Updated 2D array with rows removed
 */
export function removeTargetRows(data2D: any[][], rowIndices: number[]): any[][] {
  if (!data2D || data2D.length === 0 || rowIndices.length === 0) return data2D;
  
  const updated = JSON.parse(JSON.stringify(data2D));
  
  // Sort indices in descending order to avoid index shifting
  const sortedIndices = [...rowIndices].sort((a, b) => b - a);
  
  // Remove rows (indices are 0-based for data rows, so add 1 to skip header)
  sortedIndices.forEach(rowIndex => {
    if (rowIndex >= 0 && rowIndex < updated.length - 1) {
      // +1 because first row is header
      updated.splice(rowIndex + 1, 1);
    }
  });
  
  return updated;
}

/**
 * Reorder columns in a target table
 * Note: This modifies the display order in the grid, not the underlying structure
 * @param data2D - 2D array representation of the table (headers in first row)
 * @param fromIndex - Source column index
 * @param toIndex - Target column index
 * @returns Reordered 2D array
 */
export function reorderTargetColumns(
  data2D: any[][],
  fromIndex: number,
  toIndex: number
): any[][] {
  if (!data2D || data2D.length === 0) return data2D;
  
  const updated = JSON.parse(JSON.stringify(data2D));
  
  // Reorder each row (including header row)
  updated.forEach((row: any[]) => {
    if (row.length > fromIndex && row.length > toIndex) {
      const [moved] = row.splice(fromIndex, 1);
      row.splice(toIndex, 0, moved);
    }
  });
  
  return updated;
}

/**
 * Update a cell value in a target table
 * @param extractData - The extract data (document or study)
 * @param topicId - Topic ID
 * @param tableId - Table ID
 * @param rowId - Row ID
 * @param columnId - Column ID
 * @param value - New cell value
 * @param userId - User ID (optional, defaults to 'user-12345')
 * @param userName - User name (optional, defaults to 'User')
 * @returns Updated extract data
 */
export function updateTargetCell(
  extractData: TargetExtract,
  topicId: string,
  tableId: string,
  rowId: string,
  columnId: string,
  value: any,
  userId: string = 'user-12345',
  userName: string = 'User'
): TargetExtract {
  const updated = JSON.parse(JSON.stringify(extractData));
  
  // Find topic
  const topic = updated.topics?.find((t: any) => t.id === topicId);
  if (!topic) {
    logWarning(`Topic not found: ${topicId}`, "target-table-processing", undefined);
    return updated;
  }
  
  // Handle grouper table
  if (tableId === 'grouper') {
    // TODO: Implement grouper cell update if needed
    logWarning('Grouper table cell update not implemented', "target-table-processing", undefined);
    return updated;
  }
  
  // Find table
  const table = topic.tables?.find((t: any) => t.id === tableId);
  if (!table || !Array.isArray(table.rows)) {
    logWarning(`Table not found or has no rows: ${tableId}`, "target-table-processing", undefined);
    return updated;
  }
  
  // Find row
  const row = table.rows.find((r: any) => r.id === rowId);
  if (!row) {
    logWarning(`Row not found: ${rowId}`, "target-table-processing", undefined);
    return updated;
  }
  
  // Ensure cells array exists
  if (!Array.isArray(row.cells)) {
    row.cells = [];
  }
  
  // Remove any grouper_name cells (they shouldn't be stored)
  row.cells = row.cells.filter((c: any) => c.id !== 'grouper_name');
  
  // Find or create cell
  let cell = row.cells.find((c: any) => c.id === columnId);
  if (!cell) {
    cell = { id: columnId };
    row.cells.push(cell);
  }
  
  // Update cell value
  cell.value = value;
  
  // Update sourceInfo to mark as user input
  const now = new Date().toISOString();
  cell.sourceInfo = {
    sourceType: 'user',
    sourceID: userId,
    sourceName: userName,
    sourceDate: now
  };
  
  // Preserve reviewInfo if it exists, otherwise initialize as empty object
  if (!cell.reviewInfo) {
    cell.reviewInfo = {};
  }
  
  // Note: confirmInfo is intentionally NOT preserved
  
  return updated;
}

/**
 * Convert 2D array back to reference format
 * @param data2D - 2D array with headers in first row
 * @param referenceId - Reference ID (e.g., 'bibliography', 'treatment_arms')
 * @param projectStructure - Project structure for column definitions
 * @param extractData - Existing extract data to preserve row IDs
 * @returns Reference data in extract format
 */
export function arrayToReference(
  data2D: any[][],
  referenceId: string,
  projectStructure: any,
  extractData: any
): any {
  if (!data2D || data2D.length === 0) return { rows: [] };
  
  // Find reference structure
  const referenceStructure = projectStructure.references?.find((r: any) => r.id === referenceId);
  if (!referenceStructure) return { rows: [] };
  
  // Get existing reference data to preserve row IDs
  const existingReference = extractData.references?.find((r: any) => r.id === referenceId);
  const existingRows = existingReference?.rows || [];
  
  const headers = data2D[0];
  const dataRows = data2D.slice(1);
  
  // Get columns from structure - can be directly in referenceStructure or in referenceStructure.structure
  const columns = referenceStructure.columns || referenceStructure.structure?.columns || [];
  
  // Build a map of existing rows by ID for better matching
  const existingRowsById = new Map<string, any>();
  existingRows.forEach((row: any) => {
    if (row.id) {
      existingRowsById.set(row.id, row);
    }
  });
  
  const rows = dataRows.map((rowData: any[], rowIndex: number) => {
    // Try to preserve existing row ID by position first, then by matching GUIDs
    const existingRowByIndex = existingRows[rowIndex];
    let rowId: string;
    let existingRow: any = null;
    
    if (existingRowByIndex?.id) {
      // Try to match by position first
      rowId = existingRowByIndex.id;
      existingRow = existingRowByIndex;
    } else {
      // Generate GUID for new rows
      rowId = generateUUID();
    }
    
    const cells: any[] = [];
    
    if (columns.length > 0) {
      // Use structure columns - ensure all columns are present
      columns.forEach((column: any, colIndex: number) => {
        // Get value from rowData at the column index
        // Headers are labels, so we need to map by column order, not by header match
        const value = rowData[colIndex] ?? '';
        
        // Try to find existing cell by ID
        const existingCell = existingRow?.cells?.find((c: any) => c.id === column.id);
        
        // Build cell object, preserving all metadata properties if they exist
        const cell: any = {
          id: column.id,
          value: value || '' // Ensure empty string for empty cells
        };
        
        // Preserve sourceInfo if it exists - use deep copy to preserve all properties
        if (existingCell && 'sourceInfo' in existingCell && existingCell.sourceInfo !== undefined) {
          cell.sourceInfo = JSON.parse(JSON.stringify(existingCell.sourceInfo));
        }
        
        // Preserve reviewInfo if it exists (even if it's an empty object)
        if (existingCell && 'reviewInfo' in existingCell && existingCell.reviewInfo !== undefined) {
          cell.reviewInfo = JSON.parse(JSON.stringify(existingCell.reviewInfo));
        }
        
        // Note: confirmInfo is intentionally NOT preserved - only sourceInfo and reviewInfo are kept
        
        cells.push(cell);
      });
    } else {
      // Infer columns from headers - less reliable, but fallback
      headers.forEach((header: string, colIndex: number) => {
        const value = rowData[colIndex] ?? '';
        // Try to match cell by header (which might be label or ID)
        const existingCell = existingRow?.cells?.find((c: any) => c.id === header || c.id === header.toLowerCase());
        
        // Build cell object, preserving all metadata properties if they exist
        const cell: any = {
          id: header.toLowerCase().replace(/\s+/g, '_'), // Normalize header to ID format
          value: value || '' // Ensure empty string for empty cells
        };
        
        // Preserve sourceInfo if it exists - use deep copy to preserve all properties
        if (existingCell && 'sourceInfo' in existingCell && existingCell.sourceInfo !== undefined) {
          cell.sourceInfo = JSON.parse(JSON.stringify(existingCell.sourceInfo));
        }
        
        // Preserve reviewInfo if it exists (even if it's an empty object)
        if (existingCell && 'reviewInfo' in existingCell && existingCell.reviewInfo !== undefined) {
          cell.reviewInfo = JSON.parse(JSON.stringify(existingCell.reviewInfo));
        }
        
        // Note: confirmInfo is intentionally NOT preserved - only sourceInfo and reviewInfo are kept
        
        cells.push(cell);
      });
    }
    
    return {
      id: rowId,
      cells: cells
    };
  });
  
  return { rows };
}

/**
 * Update a cell value in a reference
 * @param extractData - The extract data (document or study)
 * @param referenceId - Reference ID
 * @param rowId - Row ID
 * @param columnId - Column ID
 * @param value - New cell value
 * @param userId - User ID (optional, defaults to 'user-12345')
 * @param userName - User name (optional, defaults to 'User')
 * @returns Updated extract data
 */
export function updateReferenceCell(
  extractData: TargetExtract,
  referenceId: string,
  rowId: string,
  columnId: string,
  value: any,
  userId: string = 'user-12345',
  userName: string = 'User'
): TargetExtract {
  const updated = JSON.parse(JSON.stringify(extractData));
  
  // Find reference
  const reference = updated.references?.find((r: any) => r.id === referenceId);
  if (!reference || !Array.isArray(reference.rows)) {
    logWarning(`Reference not found or has no rows: ${referenceId}`, "target-table-processing", undefined);
    return updated;
  }
  
  // Find row
  const row = reference.rows.find((r: any) => r.id === rowId);
  if (!row) {
    logWarning(`Row not found: ${rowId}`, "target-table-processing", undefined);
    return updated;
  }
  
  // Ensure cells array exists
  if (!Array.isArray(row.cells)) {
    row.cells = [];
  }
  
  // Find or create cell
  let cell = row.cells.find((c: any) => c.id === columnId);
  if (!cell) {
    cell = { id: columnId };
    row.cells.push(cell);
  }
  
  // Update cell value
  cell.value = value;
  
  // Update sourceInfo to mark as user input
  const now = new Date().toISOString();
  cell.sourceInfo = {
    sourceType: 'user',
    sourceID: userId,
    sourceName: userName,
    sourceDate: now
  };
  
  // Preserve reviewInfo if it exists, otherwise initialize as empty object
  if (!cell.reviewInfo) {
    cell.reviewInfo = {};
  }
  
  // Note: confirmInfo is intentionally NOT preserved
  
  return updated;
}

